// ============================================================================
// LoginScreen — v3 visual (UX bolder-refresh, 2026-05-29)
//
// Split-screen premium login: a saturated Spectropharma-gradient brand hero
// on one side, a clean sign-in panel on the other. Collapses to a single
// column on mobile (gradient band on top, card below). First pilot of the
// "lean into the brand gradient" direction (docs/proposals/UX_REFRESH_DIRECTION.md).
//
// Auth logic is UNCHANGED from v2 — single-button SSO via useSession().login().
// Only the presentation changed.
//
// Owner: UX bolder-refresh. URS rows: AUTH-001, I18N-001..002, UX-001..005,
// UX-019..020 (RTL).
// ============================================================================
import { useTranslation } from 'react-i18next';
import { useSession } from '../../auth/SessionProvider';
import styles from './LoginScreen.module.css';

export function LoginScreen() {
  const { t } = useTranslation();
  const { login } = useSession();
  const params = new URLSearchParams(window.location.search);
  const returnTo = params.get('returnTo') ?? '/';
  const errored = params.get('error');

  const onClick = () => {
    void login(returnTo);
  };

  return (
    <main className={styles.screen}>
      {/* ---- Brand hero panel (the bold gradient "wow") ---- */}
      <aside className={styles.brandPanel}>
        <div className={styles.brandGlow} aria-hidden="true" />

        {/* QWise platform masthead — the product brand above the tenant.
            Real no-BG QWise logo placed directly on the gradient with a soft
            drop-shadow for legibility (no chip / no background). */}
        <div className={styles.platformLockup}>
          <img
            src="/brand/qwise-logo.svg"
            alt="QWise"
            className={styles.qwiseIcon}
            width={44}
            height={44}
            decoding="async"
          />
          <span className={styles.platformText}>
            <span className={styles.qwiseWordmark}>QWise</span>
            <span className={styles.qwiseTagline}>
              {t('app.qwiseSlogan', { defaultValue: 'Quality. Digital. Intelligent.' })}
            </span>
          </span>
        </div>

        <div className={styles.brandContent}>
          <span className={styles.brandMark}>Spectropharma</span>
          <p className={styles.brandHeadline}>
            {t('auth.brandHeadline', { defaultValue: 'Quality, quantified.' })}
          </p>
          <p className={styles.brandSub}>
            {t('auth.brandSub', {
              defaultValue:
                'Documents, deviations, CAPAs, suppliers, and the cost of quality — one audited platform, end to end.',
            })}
          </p>
          <ul className={styles.valueProps}>
            <li>
              <span className={styles.vpIcon} aria-hidden="true">✓</span>
              {t('auth.valueProp1', { defaultValue: '21 CFR Part 11 electronic signatures' })}
            </li>
            <li>
              <span className={styles.vpIcon} aria-hidden="true">✓</span>
              {t('auth.valueProp2', { defaultValue: 'AI-assisted inspection & review' })}
            </li>
            <li>
              <span className={styles.vpIcon} aria-hidden="true">✓</span>
              {t('auth.valueProp3', { defaultValue: 'Cost of Quality, in real time' })}
            </li>
          </ul>
        </div>
        <div className={styles.brandCompliance} aria-hidden="true">
          {t('auth.compliance_note', { defaultValue: 'GMP · 21 CFR Part 11 · EU GMP Annex 11' })}
        </div>
      </aside>

      {/* ---- Sign-in panel ---- */}
      <section className={styles.authPanel}>
        <div className={styles.card}>
          <img
            src="/brand/spectropharma-wordmark.png"
            alt="Spectropharma"
            className={styles.wordmark}
            width={240}
            height={39}
            decoding="async"
          />
          <p className={styles.tagline}>
            {t('app.tagline', {
              defaultValue: 'QWise Quality Platform · eDMS · eQMS · AI Inspector',
            })}
          </p>

          <h1 className={styles.heading}>{t('auth.heading')}</h1>
          <p className={styles.subheading}>{t('auth.subheading')}</p>

          {errored && (
            <div role="alert" data-testid="login-error" className={styles.errorBanner}>
              {t('auth.login_failed', { defaultValue: 'Sign-in failed. Please try again.' })}
            </div>
          )}

          <button
            type="button"
            onClick={onClick}
            className={styles.signInButton}
            aria-label={t('auth.sso_button')}
          >
            {t('auth.sso_button')}
          </button>
          <span className={styles.buttonHint}>
            {t('auth.sso_hint', { defaultValue: 'Single sign-on via your organisation' })}
          </span>

          <footer className={styles.footer}>
            <span className={styles.poweredBy} aria-label="Powered by QWise">
              {t('auth.poweredBy', { defaultValue: 'Powered by' })}
              <span className={styles.qwiseLogo} aria-hidden="true">
                <svg viewBox="0 0 28 28" width="16" height="16" focusable="false">
                  <defs>
                    <linearGradient id="qwLoginGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#1DD1A1" />
                      <stop offset="55%" stopColor="#22D3EE" />
                      <stop offset="100%" stopColor="#0A84FF" />
                    </linearGradient>
                  </defs>
                  <circle cx="12.5" cy="12.5" r="10" stroke="url(#qwLoginGrad)" strokeWidth="2.5" fill="none" />
                  <rect x="8" y="10" width="2" height="2" fill="#22D3EE" />
                  <rect x="11" y="12" width="2" height="2" fill="#0A84FF" />
                  <rect x="8" y="14" width="2" height="2" fill="#0A84FF" />
                  <rect x="11" y="16" width="2" height="2" fill="#22D3EE" />
                  <path d="M16 17 L24 25 L21 25 L14 18 Z" fill="url(#qwLoginGrad)" />
                </svg>
                QWise
              </span>
            </span>
            <span className={styles.complianceLine}>
              {t('auth.compliance_note', {
                defaultValue: 'GMP · 21 CFR Part 11 · EU GMP Annex 11',
              })}
            </span>
          </footer>
        </div>
      </section>
    </main>
  );
}
